
import { Component, Vue } from 'vue-property-decorator';
import Button from '@/components/UI/button/Button.vue';
import BackButton from '@/components/UI/backButton/BackButton.vue';
import CustomerDetailsFieldGroup from '@/components/fieldGroups/CustomerDetailsFieldGroup.vue';
import ProductsWarehouse from '@/components/jobBooking/Goods/ProductsWarehouse.vue';
import JobBookingWarehouseFieldSet from '@/views/JobBooking/JobBookingAdvanced/JobBookingWarehouseFieldSet.vue';
import { getReferencesParamsWrapper } from '@/helpers/booking';
import { statesTimeZonesList, timeZoneDefault } from '@/utils/PortalConstants';
import { User, UserDataAdvancedJB } from '@/models/main';
import { AddressBookLookupData } from '@/models/addressBook';
import { mapState } from 'vuex';
import { format, utcToZonedTime } from 'date-fns-tz';
import { notifyNetworkSuccess } from '@/helpers/notifiers';
import { fetchBookJobWhToSameWh, fetchBookJobWhToWh, fetchGetAddressLookup } from '@/helpers/fetchers';
import { BookingError } from '@/helpers/throwables';
import { JobProducts, JobSchema, JobStop } from '@/models/jobData';
import { getUserDataForNonStaffUsers } from '@/utils/Common';
import { convertTimeStringTo24Hours } from '@/utils/InputProcessingUtilities';

@Component({
  components: {
    Button,
    BackButton,
    CustomerDetailsFieldGroup,
    ProductsWarehouse,
    JobBookingWarehouseFieldSet,
  },
  computed: {
    ...mapState('moduleUser', { user: 'user' }),
  },
})
export default class JobBookingWarehouseToWarehouse extends Vue {
  private user!: User;

  public loading = false;
  public loadingData = false;
  public isSameWH = false;
  public localStorageName = '';
  public date!: string;
  public today!: string;

  private userDataDefault: UserDataAdvancedJB = {
    customerCode: '',
    caller: '',
    phone_number: '',
    reference1: '',
    reference2: '',
    reference3: '',
    ref1_prompt: 'Reference 1',
    ref2_prompt: 'Reference 2',
    ref3_prompt: 'Reference 3',
    ref1_required: false,
    ref2_required: false,
    ref3_required: false,
  };
  public userData: UserDataAdvancedJB = { ...this.userDataDefault };

  public warehouses: AddressBookLookupData[] = [];
  public addressesWarehouses: AddressBookLookupData[] = [];

  private defaultTableValue: JobProducts = {
    model: '',
    description: '',
    serial: '',
    quantity: 1,
    installation: 'DLVONLY',
    from_stop: 1,
    to_stop: 2,
    weight: 0,
    runup: false,
    returns: '',
    model_id: null,
    quant_id: null,
  };
  public products: JobProducts[] = [{ ...this.defaultTableValue }];

  public errorFromAPIResponse = '';

  public async setCustomerCode(customerCode: string): Promise<void> {
    if (customerCode === this.userData.customerCode) {
      return;
    }
    this.userData.customerCode = customerCode;
    await getReferencesParamsWrapper(this.userData.customerCode, this.userData);
  }

  public handleDateChange(date: string): void {
    this.date = date;
    this.formChanged();
  }

  public changeProducts(products: JobProducts[]): void {
    this.products = products;
    this.formChanged();
  }

  public formChanged(): void {
    this.writeJobToLocalStorage();
    this.clearError();
  }

  public stopsChanged(data): void {
    this.warehouses = data;
    this.writeJobToLocalStorage();
    this.clearError();
  }

  public clearForm(): void {
    const form = this.$refs.form as HTMLFormElement;
    form.resetValidation();
    this.date = this.today;
    this.userData = { ...this.userDataDefault };
    this.warehouses = [];
    this.products = [{ ...this.defaultTableValue }];
    this.clearError();
    this.errorFromAPIResponse = '';
    if (this.$ability.can('staff', 'Job')) {
      this.userData = { ...this.userDataDefault };
    } else {
      this.userData.reference1 = '';
      this.userData.reference2 = '';
      this.userData.reference3 = '';
    }
    localStorage.removeItem(this.localStorageName);
  }

  private clearError(): void {
    if (this.errorFromAPIResponse.length > 0) {
      this.errorFromAPIResponse = '';
    }
  }

  public writeJobToLocalStorage(): void {
    const localStorageName = this.isSameWH ? 'jobDataWHSameWH' : 'jobDataWHToWH';
    const localStorageJobData = {
      userData: this.userData,
      stops: this.warehouses,
      date: this.date,
      products: this.products,
    };
    localStorage.setItem(localStorageName, JSON.stringify(localStorageJobData));
  }

  private async getAddressBookWrapper(code: string): Promise<void> {
    await fetchGetAddressLookup(code).then(res => {
      this.addressesWarehouses = res.filter(x => x.warehouse);
    });
  }

  private populateDataFromLocalStorage(): void {
    const localStorageName = this.isSameWH ? 'jobDataWHSameWH' : 'jobDataWHToWH';
    const writtenJobData = localStorage.getItem(localStorageName);
    if (writtenJobData) {
      const localStorageJobData = JSON.parse(writtenJobData);
      this.userData = localStorageJobData.userData || this.userDataDefault;
      this.warehouses = localStorageJobData.stops || [];
      this.date = localStorageJobData.date || this.date;
      this.products = localStorageJobData.products || [];
    }
  }

  private buildJob(): JobSchema[] {
    let stops: JobStop[] = this.warehouses.map((warehouse, i) => {
      const stopState = warehouse.suburb.state;
      const timeZone = stopState ? statesTimeZonesList[stopState].timezone : 'Australia/Sydney';
      const zoneString = format(utcToZonedTime(new Date(this.date), timeZone), 'xxxxx', { timeZone });
      return {
        name: warehouse.name,
        suburb: warehouse.suburb.name,
        state: warehouse.suburb.state,
        postcode: warehouse.suburb.postcode,
        address_line1: warehouse.line1,
        address_line2: warehouse.line2 || '',
        warehouse: true,
        attributes: [],
        number: i + 1,
        note_text: warehouse.note_text,
        note_name: warehouse.note_name,
        note_phone: warehouse.note_phone,
        note_email: '',
        time: {
          earliest: this.date + 'T' + convertTimeStringTo24Hours('07:00 AM') + zoneString,
          latest: this.date + 'T' + convertTimeStringTo24Hours('05:00 PM') + zoneString,
        },
      };
    });
    if (this.isSameWH) {
      stops = [
        { ...stops[0] },
        {
          ...stops[0],
          number: 2,
        },
      ];
    }

    const products = this.products;

    return [
      {
        schema_version: '1.0',
        customer_code: this.userData.customerCode,
        caller: this.userData.caller,
        phone: this.userData.phone_number,
        source: 'WEB',
        REF1: this.userData.reference1,
        REF2: this.userData.reference2,
        REF3: this.userData.reference3,
        stops: stops,
        products,
      },
    ];
  }

  /**
   * This function is called when the user clicks the "Book Job" button
   * and the form is valid. It sends a request to the server to book the job.
   * If the request is successful, it redirects the user to the detailed job view.
   * If the request fails, it displays an error message or shows a notification.
   */
  private async bookValidatedJob(): Promise<void> {
    this.loading = true;
    const jobData = this.buildJob();
    let promiseRunner: Promise<(string | number)[]>;
    if (this.isSameWH) {
      promiseRunner = fetchBookJobWhToSameWh(jobData);
    } else {
      promiseRunner = fetchBookJobWhToWh(jobData);
    }

    await promiseRunner
      .then(data => {
        // If the request is successful, navigate to the detailed job page and clear the local storage
        this.$router.push({
          path: `/detailed-job?job_id=${data[0]}&job_date=${data[1]}`,
        });

        // Clear quant cart if booking came from quant page
        const fromQuantPage = this.$route.query.fromQuantPage === 'true';
        if (fromQuantPage) {
          this.$store.dispatch('moduleQuantCart/clearCart');
        }

        localStorage.removeItem(this.localStorageName);
        notifyNetworkSuccess({ text: 'Job booked successfully' });
      })
      .catch(err => {
        // If the request fails due to business rules validation, display an error message
        if (err instanceof BookingError) {
          this.errorFromAPIResponse = err.message;
          return;
        }
        // This will be Uncaught (in promise) error
        throw err;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public bookJob(): void {
    const form = this.$refs.form as HTMLFormElement;
    if (form.validate()) {
      this.bookValidatedJob();
    } else {
      this.$nextTick(() => {
        const el = this.$el.querySelector('.v-messages.error--text:first-of-type') as HTMLElement;
        if (el) {
          this.$vuetify.goTo(el, { offset: 100 });
        }
      });
    }
  }

  private async mounted(): Promise<void> {
    this.loadingData = true;
    await this.getAddressBookWrapper(this.user.customerCode);
    this.populateDataFromLocalStorage();
    if (!this.$ability.can('staff', 'Job')) {
      this.userData = await getUserDataForNonStaffUsers(this.userData, this.user);
    }
    if (this.userData.customerCode) {
      await getReferencesParamsWrapper(this.userData.customerCode, this.userData);
    }
    this.loadingData = false;
  }

  private created(): void {
    if (this.$route.meta?.isSameWH) {
      this.isSameWH = this.$route.meta.isSameWH;
    }
    this.localStorageName = this.isSameWH ? 'jobDataWHSameWH' : 'jobDataWHToWH';
    const userTimeZone = this.user.timezone || timeZoneDefault;
    this.date = this.today = format(utcToZonedTime(new Date(), userTimeZone), 'yyyy-MM-dd');
  }
}
